/**
 * DashJS media player.
 */
import '../../../../../plugins/dashjs/dash.all.min-es5';
import AnalyticsInstance from '@shared/utils/analytics';
import DeviceConfig from '@devices/config';
import ErrorBoundary from '../errorBoundary';
import ListenerManager from './listenerManager';
import MediaManager from './mediaManager';
import Subtitle from '@components/atoms/text/subtitle';
import Youbora from '../../../../classes/youbora';
import displayError from '@shared/actions/player/displayError';
import { IPlayerError } from '@shared/types/interfaces/error';
import { PlayerError, PlayerType } from '@shared/types/enums/player';
import { MediaPlayerProps as Props } from '@shared/types/props/player';
import { clearError, mediaSelector } from '@shared/store/slices/media.slice';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';

/**
 * Constants.
 *
 * @constant {number} LICENSE_LIMIT - The maximum number of license requests.
 * @constant {number} LICENSE_OFFSET - The offset for license requests.
 * @constant {number} ZAPPER_DELAY - The delay for zapper.
 */
const LICENSE_LIMIT = 20;
const LICENSE_OFFSET = 60 * 1000;
const ZAPPER_DELAY = 500;

/**
 * DashJS media player component.
 */
export default function MediaPlayer({
  asset,
  channel,
  drm,
  headers,
  pilot,
  position,
  source,
}: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { pauseAd } = useAppSelector(adsSelector);
  const { startover: rawStartover, subtitles } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(routerSelector);

  // States
  const [loaded, setLoaded] = useState(false);

  // Refs
  const licenseFailed = useRef(false);
  const licenseRequestCount = useRef(0);
  const licenseRequestDate = useRef<number | null>(null);
  const player = useRef<dashjs.MediaPlayerClass | null>(null);
  const cues = useRef<HTMLDivElement | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const youbora = useRef<Youbora | null>(null);
  const autoplay = useMemo(() => !pauseAd, []);
  const startover = useMemo(
    () => rawStartover || params.current?.restart || false,
    [params, rawStartover]
  );
  const props = {
    dash: player?.current,
    video: video?.current,
    source,
    ...position,
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Player and Youbora destruction.
   */
  useEffect(() => {
    // Player and Youbora destruction
    return () => {
      player.current?.destroy();
      player.current = null;
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, []);

  /**
   * Youbora initialization.
   */
  useEffect(() => {
    if (player.current) {
      youbora.current = new Youbora({
        asset,
        channel,
        player: { instance: player.current, type: PlayerType.DASHJS },
      });
      youbora.current?.start();
    }
    return () => {
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, [startover]);

  /**
   * Player initialization.
   */
  useEffect(() => {
    // Initialize default error
    const error: IPlayerError = {
      player: PlayerType.DASHJS,
      type: PlayerError.LOAD,
    };

    let playerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      setLoaded(false);
      playerTimeout = setTimeout(() => {
        // Player creation
        if (!player.current) {
          player.current = dashjs?.MediaPlayer().create();
        }

        // Reset license date and count
        licenseRequestCount.current = 0;
        licenseRequestDate.current = null;

        // Destroy previous youbora instance
        youbora.current?.destroy();
        youbora.current = null;

        if (player.current && source) {
          // Required headers
          const {
            accessToken,
            contentType,
            deviceId,
            planId,
            platform,
            userId,
          } = headers || {};

          // Player configuration
          if (drm) {
            const protData = {
              [drm.key]: {
                audioRobustness: 'SW_SECURE_CRYPTO',
                httpRequestHeaders: {
                  'content-type': contentType,
                  'x-access-token': accessToken,
                  'x-tvup-device': deviceId,
                  'x-tvup-planid': planId,
                  'x-tvup-platform': platform,
                  'x-tvup-userid': userId,
                },
                serverURL: drm.license,
                videoRobustness: 'SW_SECURE_DECODE',
              },
            };
            player.current?.setProtectionData(protData);
          }
          player.current?.updateSettings({
            errors: {
              recoverAttempts: {
                mediaErrorDecode: 5,
              },
            },
            streaming: {
              abr: {
                useDefaultABRRules: true,
                ABRStrategy: 'abrThroughput',
                additionalAbrRules: {
                  insufficientBufferRule: true,
                  switchHistoryRule: false,
                  droppedFramesRule: false,
                  abandonRequestsRule: false,
                },
              },
              fragmentRequestTimeout: 20000,
              manifestUpdateRetryInterval: 2000,
              retryIntervals: {
                MPD: 3000,
                XLinkExpansion: 3000,
                InitializationSegment: 3000,
                IndexSegment: 3000,
                MediaSegment: 3000,
                BitstreamSwitchingSegment: 3000,
                other: 3000,
              },
              retryAttempts: {
                MPD: 15,
                XLinkExpansion: 15,
                InitializationSegment: 15,
                IndexSegment: 15,
                MediaSegment: 15,
                BitstreamSwitchingSegment: 15,
                FragmentInfoSegment: 15,
                other: 15,
              },
              text: {
                defaultEnabled: false,
              },
            },
          });

          // Player and Youbora initialization
          if (video.current) {
            youbora.current = new Youbora({
              asset,
              channel,
              player: { instance: player.current, type: PlayerType.DASHJS },
            });
            if (DeviceConfig.info.os === 'Tizen') {
              player.current.registerLicenseRequestFilter(
                (xhr: { url: string }) => {
                  if (!licenseRequestDate.current) {
                    licenseRequestDate.current = Date.now();
                  } else {
                    const currentDate = Date.now();
                    if (
                      currentDate - licenseRequestDate.current <
                      LICENSE_OFFSET
                    ) {
                      if (licenseRequestCount.current < LICENSE_LIMIT) {
                        licenseRequestCount.current++;
                      } else {
                        licenseFailed.current = true;
                        xhr.url = 'about:blank';
                      }
                    } else {
                      licenseRequestCount.current = 0;
                      licenseRequestDate.current = currentDate;
                      if (licenseFailed.current) {
                        dispatch(clearError());
                      }
                    }
                  }
                  return Promise.resolve();
                }
              );
            }
            AnalyticsInstance.getOmnitrackManager()?.attachDashJSPlayer(
              player.current,
              video.current
            );
            player.current?.initialize(
              video.current,
              source,
              autoplay,
              position.keepWatching || position.bookmark || position.start
            );
            player.current?.attachTTMLRenderingDiv(cues.current);
            setLoaded(true);
          }
        } else {
          const text = 'No source available';
          error.detail = text;
          console.error('[DashJS]', text);
          displayError(error);
        }
      }, ZAPPER_DELAY);
    } catch (e) {
      const text = 'Error loading player';
      error.detail = text;
      console.error('[DashJS]', text, e);
      displayError(error);
    }

    return () => {
      clearTimeout(playerTimeout);
      if (player.current?.isReady()) {
        player.current?.attachSource('');
        setLoaded(false);
      }
    };
  }, [source]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <>
      <video
        autoPlay={autoplay}
        id={'video'}
        ref={video}
        height={'100%'}
        width={'100%'}
      />
      {loaded ? (
        <ErrorBoundary>
          <ListenerManager {...props} />
          <MediaManager {...props} />
        </ErrorBoundary>
      ) : null}
      {/* Subtitle rendering div - Must not be conditionally rendered */}
      <Subtitle
        className={pilot ? 'pilot' : ''}
        ref={cues}
        visible={!!subtitles?.id || subtitles?.id === 0}
      />
    </>
  );
}
